import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import metadataApi from '../../api/metadata';
import astroApi from '../../api/astro';

const ExplorerForQueryBuilder = ({ onItemClick }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [expandedSchemas, setExpandedSchemas] = useState({});
  const [expandedTables, setExpandedTables] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [tablesBySchema, setTablesBySchema] = useState({});
  const [columnsByTable, setColumnsByTable] = useState({});
  const [loadingSchema, setLoadingSchema] = useState(null);
  const [loadingTable, setLoadingTable] = useState(null);
  const [astroTables, setAstroTables] = useState([]);
  const [astroTablesLoaded, setAstroTablesLoaded] = useState(false);

  // Load list of schemas
  useEffect(() => {
    const fetchSchemas = async () => {
      try {
        setLoading(true);
        setError(null);
        const schemasList = await metadataApi.getSchemas();
        setSchemas(schemasList);
      } catch (err) {
        console.error('Failed to fetch schemas:', err);
        setError('Failed to load database schemas. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSchemas();
  }, []);

  // Function to load tables for a schema when expanded
  const loadTablesForSchema = async (schema) => {
    if (tablesBySchema[schema]) return; // Already loaded
    
    try {
      setLoadingSchema(schema);
      const tables = await metadataApi.getTables(schema);
      setTablesBySchema(prev => ({
        ...prev,
        [schema]: tables
      }));
    } catch (err) {
      console.error(`Failed to load tables for schema ${schema}:`, err);
      setTablesBySchema(prev => ({
        ...prev,
        [schema]: []
      }));
    } finally {
      setLoadingSchema(null);
    }
  };

  // Function to load columns for a table when expanded
  const loadColumnsForTable = async (schema, table) => {
    const tableKey = `${schema}.${table}`;
    if (columnsByTable[tableKey]) return; // Already loaded
    
    try {
      setLoadingTable(tableKey);
      const columns = await metadataApi.getColumns(schema, table);
      setColumnsByTable(prev => ({
        ...prev,
        [tableKey]: columns
      }));
    } catch (err) {
      console.error(`Failed to load columns for ${schema}.${table}:`, err);
      setColumnsByTable(prev => ({
        ...prev,
        [tableKey]: []
      }));
    } finally {
      setLoadingTable(null);
    }
  };

  // Load astrotables only once when first schema is expanded
  const loadAstroTablesOnce = async () => {
    if (astroTablesLoaded) return;
    
    try {
      const astroTablesList = await astroApi.getAstroTables();
      setAstroTables(astroTablesList);
      setAstroTablesLoaded(true);
    } catch (err) {
      console.error('Failed to fetch astrotables:', err);
      // Don't set error state as this is not critical for basic functionality
    }
  };

  // Helper function to find astrotable with documentation_route
  const findAstroTableWithDocs = (schemaName, tableName) => {
    if (!astroTables || astroTables.length === 0) {
      return null;
    }
    
    const result = astroTables.find(astroTable => {
      const matchesSchema = astroTable.schema_name === schemaName;
      const matchesTable = astroTable.table_name === tableName;
      const hasDocRoute = astroTable.documentation_route && astroTable.documentation_route.trim() !== '';
      
      return matchesSchema && matchesTable && hasDocRoute;
    });
    
    return result;
  };

  const toggleSchemaExpanded = async (schema) => {
    const newExpandedState = !expandedSchemas[schema];
    
    setExpandedSchemas(prev => ({
      ...prev,
      [schema]: newExpandedState
    }));
    
    if (newExpandedState) {
      await loadAstroTablesOnce(); // Load astrotables only once when first schema is expanded
      await loadTablesForSchema(schema);
    }
  };

  const toggleTableExpanded = async (schema, table) => {
    const tableKey = `${schema}.${table}`;
    const newExpandedState = !expandedTables[tableKey];
    
    setExpandedTables(prev => ({
      ...prev,
      [tableKey]: newExpandedState
    }));
    
    if (newExpandedState) {
      await loadColumnsForTable(schema, table);
    }
  };

  // Handler for double-clicking items to insert into editor
  const handleItemDoubleClick = (e, text) => {
    e.stopPropagation(); // Prevent the click from triggering the toggle
    if (onItemClick) {
      onItemClick(text);
    }
  };


  // Filter database objects based on search query
  const filterSchemas = () => {
    if (!searchQuery.trim()) {
      return schemas;
    }
    
    const query = searchQuery.toLowerCase();
    return schemas.filter(schema => {
      // Check if schema name matches
      if (schema.toLowerCase().includes(query)) {
        return true;
      }
      
      // Check if any tables in this schema match
      const schemaTables = tablesBySchema[schema] || [];
      return schemaTables.some(table => {
        // Check table name
        if (table.toLowerCase().includes(query)) {
          return true;
        }
        
        // Check columns in this table if they're loaded
        const tableKey = `${schema}.${table}`;
        const columns = columnsByTable[tableKey] || [];
        return columns.some(col => 
          col.name.toLowerCase().includes(query) || 
          col.data_type.toLowerCase().includes(query)
        );
      });
    });
  };

  const filteredSchemas = filterSchemas();

  if (loading) {
    return (
      <div className="p-4 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md h-full">
        <div className="flex justify-center">
          <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="mt-2 text-center text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Loading database structure...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md h-full">
        <div className="p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md border border-status-error-light-text/20 dark:border-status-error-dark-text/20">
          <p className="font-medium">Error:</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md h-full">
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search schemas, tables, or columns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background"
        />

        {/* Legend */}
        <div className="flex items-center justify-center mt-3 text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted space-x-4">
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-1 text-ui-light-text-muted dark:text-ui-dark-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
            </svg>
            Schema
          </div>
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-1 text-ui-light-text-muted dark:text-ui-dark-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2z" />
            </svg>
            Table
          </div>
          <div className="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-1 text-ui-light-text-muted dark:text-ui-dark-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Column
          </div>
        </div>

        {/* Hint */}
        <p className="mt-2 text-center text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
          Double-click an item to insert it into the query
        </p>
      </div>
      
      <div className="border border-ui-light-border dark:border-ui-dark-border rounded-md">
        <div className="overflow-auto max-h-[calc(100vh-300px)]">
          {filteredSchemas.map(schemaName => {
            const isExpanded = expandedSchemas[schemaName];
            const schemaTables = tablesBySchema[schemaName] || [];
            const isLoading = loadingSchema === schemaName;
            
            return (
              <div key={schemaName} className="border-b border-ui-light-border dark:border-ui-dark-border last:border-b-0">
                <div className="flex items-center justify-between px-3 py-2">
                  <div className="flex items-center">
                    <span
                      className="mr-2 cursor-pointer"
                      onClick={() => toggleSchemaExpanded(schemaName)}
                    >
                      {isExpanded ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-ui-light-text-muted dark:text-ui-dark-text-muted" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-ui-light-text-muted dark:text-ui-dark-text-muted" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-2 text-ui-light-text-muted dark:text-ui-dark-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                    </svg>
                    <span
                      className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      onDoubleClick={(e) => handleItemDoubleClick(e, schemaName)}
                    >{schemaName}</span>
                  </div>
                  {isLoading ? (
                    <svg className="w-4 h-4 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                      {tablesBySchema[schemaName] ? `${schemaTables.length} tables` : ''}
                    </span>
                  )}
                </div>
                
                {isExpanded && (
                  <div className="pl-6 border-t border-ui-light-border dark:border-ui-dark-border">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-2">
                        <svg className="w-4 h-4 mr-2 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Loading tables...</span>
                      </div>
                    ) : schemaTables.length === 0 ? (
                      <div className="px-4 py-2 text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                        No tables available in this schema
                      </div>
                    ) : (
                      schemaTables.map(tableName => {
                        const tableKey = `${schemaName}.${tableName}`;
                        const isTableExpanded = expandedTables[tableKey];
                        const isTableLoading = loadingTable === tableKey;
                        const tableColumns = columnsByTable[tableKey] || [];
                        
                        return (
                          <div key={tableKey} className="border-b border-ui-light-border dark:border-ui-dark-border last:border-b-0">
                            <div className="flex items-center justify-between px-3 py-2">
                              <div className="flex items-center">
                                <span
                                  className="mr-2 cursor-pointer"
                                  onClick={() => toggleTableExpanded(schemaName, tableName)}
                                >
                                  {isTableExpanded ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-ui-light-text-muted dark:text-ui-dark-text-muted" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-ui-light-text-muted dark:text-ui-dark-text-muted" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </span>
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-2 text-ui-light-text-muted dark:text-ui-dark-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2z" />
                                </svg>
                                <span
                                  className="text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary cursor-pointer hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                                  onDoubleClick={(e) => handleItemDoubleClick(e, `"${schemaName}"."${tableName}"`)}
                                >{tableName}</span>
                              </div>
                              <div className="flex items-center">
                                {isTableLoading ? (
                                  <svg className="w-3 h-3 mr-2 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <span className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted mr-2">
                                    {columnsByTable[tableKey] ? `${tableColumns.length}` : ''}
                                  </span>
                                )}
                                {/* Documentation icon for astrotables with documentation_route */}
                                {findAstroTableWithDocs(schemaName, tableName) && (
                                  <Link
                                    to={findAstroTableWithDocs(schemaName, tableName).documentation_route}
                                    className="p-1 text-primary-700 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-200 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                                    title="View documentation"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                  </Link>
                                )}
                              </div>
                            </div>
                            
                            {isTableExpanded && (
                              <div className="pl-5 pt-1 pb-2 bg-ui-light-hover dark:bg-ui-dark-hover">
                                {isTableLoading ? (
                                  <div className="flex items-center justify-center py-2">
                                    <svg className="w-3 h-3 mr-2 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">Loading columns...</span>
                                  </div>
                                ) : tableColumns.length === 0 ? (
                                  <div className="px-3 py-2 text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                                    No columns available
                                  </div>
                                ) : (
                                  <div className="space-y-1">
                                    {tableColumns.map(column => (
                                      <div
                                        key={column.name}
                                        className="flex items-center justify-between px-3 py-1 text-xs hover:bg-ui-light-card dark:hover:bg-ui-dark-card cursor-pointer transition-colors"
                                        onDoubleClick={(e) => handleItemDoubleClick(e, `"${column.name}"`)}
                                      >
                                        <div className="flex items-center">
                                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-2 text-ui-light-text-muted dark:text-ui-dark-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          <span className="font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">{column.name}</span>
                                        </div>
                                        <span className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">{column.data_type}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExplorerForQueryBuilder;